import React, { useEffect, useState } from "react";
import { X } from "lucide-react";

/**
 * Hook: true when the viewport is at or below the mobile breakpoint (700 px).
 * Mirrors the @media (max-width: 700px) rules used across premium.css.
 */
export function useIsMobile() {
  const get = () =>
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(max-width: 700px)").matches;
  const [m, setM] = useState(get);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 700px)");
    const h = () => setM(mq.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return m;
}

/**
 * Responsive sheet wrapper.
 *
 *   <MobileSheet open={showFoo} onClose={() => setShowFoo(false)} title="…">
 *     <FooForm />
 *   </MobileSheet>
 *
 * Desktop (>700 px) — renders children inline (current behaviour preserved).
 * Mobile (≤700 px) — renders as a bottom-sheet over a dimmed backdrop:
 *   - tapping the backdrop closes,
 *   - the sheet scrolls internally (max-height 90vh),
 *   - safe-area padding for iOS home-bar,
 *   - includes an explicit `X` close button + title so the user always knows
 *     they are in an active form.
 *
 * `inlineWrapClassName` is applied on desktop (lets the caller keep the
 * card-style padding/background it had before).
 */
export default function MobileSheet({
  open,
  onClose,
  title,
  children,
  testId,
  inlineWrapClassName = "",
  inlineWrapStyle,
}) {
  const isMobile = useIsMobile();

  // Lock body scroll while the sheet is open on mobile so the page doesn't
  // feel "frozen behind a form" — the sheet itself owns the scroll.
  // iOS Safari ignores `overflow: hidden` on body alone; we pin position:fixed
  // and restore the original scrollY on close to keep the page settled.
  useEffect(() => {
    if (!open || !isMobile) return;
    const scrollY = window.scrollY;
    const prevPosition = document.body.style.position;
    const prevTop = document.body.style.top;
    const prevWidth = document.body.style.width;
    const prevOverflow = document.body.style.overflow;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.position = prevPosition;
      document.body.style.top = prevTop;
      document.body.style.width = prevWidth;
      document.body.style.overflow = prevOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [open, isMobile]);

  if (!open) return null;

  if (!isMobile) {
    return (
      <div data-testid={testId} className={inlineWrapClassName} style={inlineWrapStyle}>
        {children}
      </div>
    );
  }

  return (
    <div className="modal-backdrop" data-testid={testId} onClick={onClose}>
      <div
        className="modal-body"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: "90vh", overflowY: "auto", padding: 0 }}
      >
        <header
          style={{
            position: "sticky", top: 0, zIndex: 1,
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "14px 16px", borderBottom: "1px solid var(--line)",
            background: "var(--card-bg, rgba(15,15,18,.96))",
            backdropFilter: "blur(6px)",
          }}
        >
          <div
            className="font-mono"
            style={{ fontSize: 11, letterSpacing: ".15em", color: "var(--amber-400)" }}
          >
            {title || "FORM"}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid={testId ? `${testId}-close` : "mobile-sheet-close"}
            className="btn btn-ghost"
            style={{ padding: "6px 10px", lineHeight: 1, minHeight: 36 }}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </header>
        <div style={{ padding: 16 }}>{children}</div>
      </div>
    </div>
  );
}
